import React, { useEffect, useState } from "react";
import "./Blog.css";

const sections = [
  { id: "what-are", label: "What are capital allowances?" },
  { id: "what-qualify", label: "What can qualify in a care home?" },
  { id: "why-relevant", label: "Why are capital allowances relevant?" },
  { id: "older-property", label: "Can you claim on an older property?" },
  { id: "section-198", label: "What is a Section 198 election?" },
  { id: "survey", label: "Do you need a property survey?" },
  { id: "relief", label: "What relief could be available?" },
  { id: "example", label: "An illustrative example" },
  { id: "missed", label: "Common reasons claims are missed" },
  { id: "help", label: "How Total Care Advisory can help" },
];

const qualifyingItems = [
  "Electrical and lighting systems",
  "Heating and hot-water systems",
  "Air-conditioning and ventilation",
  "Lifts and lifting equipment",
  "Fire alarms and emergency lighting",
  "CCTV, security and access-control systems",
  "Nurse-call and communication systems",
  "Fitted kitchens and catering equipment",
  "Certain bathroom and sanitary installations",
  "Laundry equipment",
  "Flooring and specialist finishes",
  "Data and telecommunications systems",
  "Furniture and office equipment",
  "Alterations required to install qualifying equipment",
];

const surveyItems = [
  "A review of the property and transaction documents",
  "Identification of qualifying and non-qualifying expenditure",
  "A detailed schedule of fixtures and integral features",
  "An appropriate valuation methodology",
  "Allocation to the relevant capital allowances pools",
  "Supporting photographs and evidence",
  "Tax calculations for your accountant",
  "A report suitable for supporting the claim if HMRC raises questions",
];

const missedReasons = [
  "The purchase agreement does not provide a detailed breakdown of fixtures",
  "The accountant does not have specialist property-survey information",
  "Renovation costs are recorded as one total figure",
  "The purchaser assumes all allowances were claimed by the seller",
  "Section 198 elections and pooling requirements were not considered",
  "Historic invoices and construction records are incomplete",
  "The business assumes that an older purchase can no longer be reviewed",
];

const processSteps = [
  "An initial eligibility review",
  "Examination of the property and purchase documentation",
  "An estimate of the potential qualifying expenditure",
  "A specialist property survey, where required",
  "Preparation of a detailed capital allowances report",
  "Collaboration with your existing accountant",
  "Support with the information required to submit the claim",
];

function Blog_Capital_Allowance() {
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    // Prevent errors in environments where IntersectionObserver
    // is not available.
    if (typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      {
        rootMargin: "-100px 0px -55% 0px",
        threshold: [0.1, 0.3, 0.6],
      },
    );

    sections.forEach((section) => {
      const element = document.getElementById(section.id);

      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <main className="ca-blog">
      {/* ================= HERO ================= */}
      <section className="ca-blog-hero">
        <div className="ca-hero-overlay" />

        <div className="ca-hero-content">
          <span className="ca-category">CAPITAL ALLOWANCES</span>

          <h1>
            Capital Allowances for Care Homes:
            <span>Are You Missing Out on Valuable Tax Relief?</span>
          </h1>

          <p>
            If you own, purchase or refurbish a care home, you may be entitled
            to valuable capital allowances that have not yet been identified or
            claimed.
          </p>

          <div className="ca-blog-meta">
            <span>Care Sector Insights</span>
            <span>•</span>
            <span>Capital Allowances</span>
          </div>
        </div>
      </section>

      {/* ================= INTRO ================= */}
      <section className="ca-intro">
        <div className="ca-container">
          <div className="ca-intro-card">
            <div className="ca-intro-icon">£</div>

            <div>
              <p>
                Many care-home operators assume that their accountant will
                automatically identify every qualifying item within a property.
                However, specialist property capital allowances can require a
                detailed review of the building, its fixtures, the purchase
                documentation and previous claims.
              </p>

              <p className="ca-intro-highlight">
                As a result, significant tax relief can remain hidden within the
                property.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= MAIN CONTENT ================= */}
      <section className="ca-content-section">
        <div className="ca-container ca-layout">
          {/* ================= SIDEBAR ================= */}
          <aside className="ca-sidebar">
            <div className="ca-toc">
              <h3>In this article</h3>

              <div className="ca-toc-list">
                {sections.map((section) => (
                  <button
                    type="button"
                    key={section.id}
                    className={activeSection === section.id ? "active" : ""}
                    onClick={() => scrollToSection(section.id)}
                  >
                    {section.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="ca-sidebar-cta">
              <div className="ca-sidebar-icon">£</div>

              <h3>Could you be missing tax relief?</h3>

              <p>
                Find out whether your care property could have unclaimed capital
                allowances.
              </p>

              <a href="#contact" className="ca-sidebar-btn">
                Request Assessment
                <span>→</span>
              </a>
            </div>
          </aside>

          {/* ================= ARTICLE ================= */}
          <article className="ca-article">
            {/* 01 */}
            <section id="what-are" className="ca-article-section">
              <span className="ca-section-number">01</span>

              <h2>What are capital allowances?</h2>

              <p>
                Capital allowances are a form of tax relief available to
                businesses that incur qualifying capital expenditure. They allow
                some or all of the value of qualifying assets to be deducted
                from taxable profits, reducing the amount of Corporation Tax or
                Income Tax payable.
              </p>

              <div className="ca-info-box">
                <div className="ca-info-icon">i</div>

                <p>
                  According to{" "}
                  <a
                    href="https://www.gov.uk/capital-allowances"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    HMRC's capital allowances guidance
                  </a>
                  , relief may be available for qualifying plant, machinery,
                  equipment, fixtures and certain integral features within a
                  commercial property.
                </p>
              </div>

              <p>
                The building itself and the land will not normally qualify for
                plant and machinery allowances. However, many of the systems and
                fixtures installed within a care home may qualify.
              </p>
            </section>

            {/* 02 */}
            <section id="what-qualify" className="ca-article-section">
              <span className="ca-section-number">02</span>

              <h2>What can qualify in a care home?</h2>

              <p>
                Care homes contain a substantial amount of specialist equipment
                and building systems. Depending on the property, qualifying
                expenditure may include:
              </p>

              <div className="ca-check-grid">
                {qualifyingItems.map((item, index) => (
                  <div className="ca-check-item" key={`${item}-${index}`}>
                    <span>✓</span>
                    <p>{item}</p>
                  </div>
                ))}
              </div>

              <p>
                HMRC specifically recognises systems such as lifts, heating, air
                conditioning, hot and cold water systems, electrical systems and
                external solar shading as integral features. It also identifies
                fixtures such as fitted kitchens, bathroom suites, fire alarms
                and CCTV systems as potentially qualifying assets. Eligibility
                will depend on the facts and circumstances of each claim.
              </p>
            </section>

            {/* 03 */}
            <section id="why-relevant" className="ca-article-section">
              <span className="ca-section-number">03</span>

              <h2>
                Why are capital allowances particularly relevant to care homes?
              </h2>

              <p>
                A care home is much more than a standard commercial building. It
                often contains specialist mechanical, electrical, safety and
                care-related installations that are essential to the operation
                of the service.
              </p>

              <p>
                When a care home is purchased, the sale price will normally
                cover the entire property rather than showing a separate value
                for each qualifying asset. A specialist assessment may therefore
                be required to establish how much of the purchase price relates
                to plant, machinery, fixtures and integral features.
              </p>

              <div className="ca-highlight-card">
                <span>KEY POINT</span>

                <h3>
                  Without a specialist assessment, qualifying expenditure can
                  easily be overlooked.
                </h3>
              </div>
            </section>

            {/* 04 */}
            <section id="older-property" className="ca-article-section">
              <span className="ca-section-number">04</span>

              <h2>Can you claim on a care home purchased several years ago?</h2>

              <p className="ca-big-answer">Potentially, yes.</p>

              <p>
                If you still own the property and qualifying expenditure has not
                previously been claimed, it may be possible to identify the
                expenditure and claim writing-down allowances. HMRC confirms
                that previously unclaimed expenditure on older assets may
                sometimes be added to the appropriate capital allowances pool,
                provided there is sufficient evidence and no previous claim has
                been made.
              </p>

              <p>
                However, claims involving second-hand properties are subject to
                important rules. The purchase date, previous ownership, tax
                history, sale agreement, fixed-value requirement, pooling
                requirement and any Section 198 election may all affect the
                amount available.
              </p>

              <p>
                Buying a property five or more years ago does not automatically
                prevent a claim, but the transaction documents and tax position
                must be examined carefully.
              </p>
            </section>

            {/* 05 */}
            <section id="section-198" className="ca-article-section">
              <span className="ca-section-number">05</span>

              <h2>What is a Section 198 election?</h2>

              <p>
                When a commercial property containing fixtures changes
                ownership, the buyer and seller may agree the value allocated to
                qualifying fixtures through a Section 198 election.
              </p>

              <div className="ca-section198">
                <div className="ca-section198-badge">198</div>

                <div>
                  <h3>Why does it matter?</h3>

                  <p>
                    This election can be extremely important because it may
                    determine the amount of capital allowances that the
                    purchaser can claim.
                  </p>
                </div>
              </div>

              <p>
                HMRC recommends documenting the agreed value, relevant
                warranties and elections within the sale and purchase
                arrangements.
              </p>

              <p className="ca-warning">
                Capital allowances should therefore be considered before
                exchanging contracts wherever possible — not only after the
                acquisition has completed.
              </p>
            </section>

            {/* 06 */}
            <section id="survey" className="ca-article-section">
              <span className="ca-section-number">06</span>

              <h2>Do you need a property survey?</h2>

              <p>
                For property-based capital allowances, a specialist survey is
                often beneficial and may be necessary to prepare a robust claim.
              </p>

              <p>
                The surveyor can identify qualifying assets, examine the
                building's systems and prepare a detailed valuation. This can be
                particularly important where historic invoices do not provide an
                adequate breakdown of the expenditure.
              </p>

              <h3 className="ca-subheading">
                The final report should normally include:
              </h3>

              <div className="ca-number-list">
                {surveyItems.map((item, index) => (
                  <div className="ca-number-item" key={`${item}-${index}`}>
                    <span>{String(index + 1).padStart(2, "0")}</span>

                    <p>{item}</p>
                  </div>
                ))}
              </div>

              <p>
                A desktop assessment may initially indicate whether a claim is
                commercially worthwhile, but it should not replace appropriate
                evidence where a full survey is required.
              </p>
            </section>

            {/* 07 */}
            <section id="relief" className="ca-article-section">
              <span className="ca-section-number">07</span>

              <h2>What relief could be available?</h2>

              <p>
                The relief available depends on when the expenditure was
                incurred, the type and condition of the asset, the claimant and
                whether the property or asset was acquired new or second-hand.
              </p>

              <div className="ca-relief-grid">
                <div className="ca-relief-card">
                  <div className="ca-relief-icon">AIA</div>

                  <h3>Annual Investment Allowance</h3>

                  <p>
                    The Annual Investment Allowance can provide a 100% deduction
                    for qualifying plant and machinery expenditure, subject to
                    the applicable conditions and annual limit.
                  </p>

                  <strong>Current general AIA limit: £1 million</strong>

                  <small>
                    It may need to be shared between connected businesses.
                  </small>
                </div>

                <div className="ca-relief-card">
                  <div className="ca-relief-icon">WDA</div>

                  <h3>Writing-down allowances</h3>

                  <p>
                    Qualifying expenditure that does not receive full relief
                    immediately may be placed into the main-rate or special-rate
                    pool.
                  </p>

                  <strong>Relief is claimed over time</strong>

                  <small>
                    The applicable rate depends on the asset and circumstances.
                  </small>
                </div>

                <div className="ca-relief-card">
                  <div className="ca-relief-icon">100%</div>

                  <h3>Full expensing</h3>

                  <p>
                    Companies may be able to claim 100% relief on certain new
                    and unused main-rate plant and machinery.
                  </p>

                  <strong>Different rules can apply</strong>

                  <small>
                    Special-rate and second-hand assets can be treated
                    differently.
                  </small>
                </div>

                <div className="ca-relief-card">
                  <div className="ca-relief-icon">3%</div>

                  <h3>Structures and Buildings Allowance</h3>

                  <p>
                    Certain qualifying construction, conversion and renovation
                    expenditure on non-residential buildings may qualify.
                  </p>

                  <strong>Standard allowance: 3% per year</strong>

                  <small>
                    Subject to qualifying conditions and an allowance statement.
                  </small>
                </div>
              </div>

              <p>
                Structures and Buildings Allowance is separate from plant and
                machinery allowances and can have implications when the property
                is eventually sold. Professional advice should therefore be
                obtained before making a claim.
              </p>
            </section>

            {/* 08 */}
            <section id="example" className="ca-article-section">
              <span className="ca-section-number">08</span>

              <h2>An illustrative example</h2>

              <div className="ca-example-card">
                <div className="ca-example-header">
                  <span>ILLUSTRATIVE EXAMPLE</span>

                  <strong>£2,000,000</strong>
                </div>

                <div className="ca-example-body">
                  <p>
                    A care-home company purchases a property for{" "}
                    <strong>£2 million</strong>.
                  </p>

                  <div className="ca-example-arrow">↓</div>

                  <p>
                    Following a review of the purchase documents and a
                    specialist property survey, <strong>£500,000</strong> is
                    identified as potentially qualifying plant, machinery,
                    fixtures and integral features.
                  </p>

                  <div className="ca-tax-result">
                    <div>
                      <span>Potential qualifying expenditure</span>

                      <strong>£500,000</strong>
                    </div>

                    <div>
                      <span>Illustrative Corporation Tax rate</span>

                      <strong>25%</strong>
                    </div>

                    <div>
                      <span>Potential tax reduction</span>

                      <strong>Up to £125,000</strong>
                    </div>
                  </div>
                </div>
              </div>

              <p>
                This is only an illustration. The actual qualifying expenditure,
                timing of relief and tax saving will depend on the property,
                transaction, ownership structure, available allowances and the
                company's tax position.
              </p>
            </section>

            {/* 09 */}
            <section id="missed" className="ca-article-section">
              <span className="ca-section-number">09</span>

              <h2>Common reasons claims are missed</h2>

              <p>Capital allowances may be overlooked because:</p>

              <div className="ca-missed-list">
                {missedReasons.map((item, index) => (
                  <div className="ca-missed-item" key={`${item}-${index}`}>
                    <span>×</span>
                    <p>{item}</p>
                  </div>
                ))}
              </div>

              <p>
                A specialist capital allowances review can establish whether a
                claim remains available and whether the expected tax benefit
                justifies the cost of preparing it.
              </p>
            </section>

            {/* 10 */}
            <section id="help" className="ca-article-section">
              <span className="ca-section-number">10</span>

              <h2>How Total Care Advisory can help</h2>

              <p>
                Total Care Advisory combines knowledge of the care sector with
                specialist capital allowances support.
              </p>

              <div className="ca-process">
                {processSteps.map((step, index) => (
                  <div className="ca-process-step" key={`${step}-${index}`}>
                    <div className="ca-process-number">
                      {String(index + 1).padStart(2, "0")}
                    </div>

                    <p>{step}</p>
                  </div>
                ))}
              </div>

              <div className="ca-care-settings">
                <span>CARE SECTOR EXPERTISE</span>

                <p>
                  We understand the specialist systems found in residential,
                  nursing, dementia and learning-disability care settings. This
                  helps ensure that the property is reviewed with the
                  operational requirements of a care service in mind.
                </p>
              </div>
            </section>
          </article>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section id="contact" className="ca-final-cta">
        <div className="ca-final-pattern" />

        <div className="ca-container ca-final-content">
          <span className="ca-category">TAKE THE NEXT STEP</span>

          <h2>Request a free initial assessment</h2>

          <p>
            If you have purchased, extended or refurbished a care home, you may
            be entitled to tax relief that has not yet been claimed.
          </p>

          <p>
            Contact Total Care Advisory for an initial review of your property
            and potential capital allowances entitlement.
          </p>

          <a href="mailto:info@totalcareadvisory.co.uk" className="ca-main-btn">
            Request Your Free Capital Allowances Assessment
            <span>→</span>
          </a>
        </div>
      </section>

      {/* ================= DISCLAIMER ================= */}
      <section className="ca-disclaimer">
        <div className="ca-container">
          <p>
            This article provides general information only and does not
            constitute tax, accounting or legal advice. Eligibility and tax
            savings depend on individual circumstances. Claims should be
            reviewed by suitably qualified advisers.
          </p>
        </div>
      </section>

      {/* ================= BACK TO TOP ================= */}
      <button
        type="button"
        className="ca-back-top"
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        ↑
      </button>
    </main>
  );
}

export default Blog_Capital_Allowance;
